/**
 * GateModal — Unified freemium gate modal
 * Three views: Signup (guest) | Limit Reached (free user) | Pass Required (needs upgrade)
 * The user's inputs are kept (estimate drafts live in session storage), so they can come back after.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import TimerOffRoundedIcon from '@mui/icons-material/TimerOffRounded';
import { useLanguage } from '../../utils/LanguageContext';
import { GATE_REASONS, GATE_TEXTS } from '../../constants/gateConfig';
import { IconTile } from '../ui';
import { useSubscription } from '../../contexts/SubscriptionContext';

const GateModal = ({ open, onClose, gateState }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const txt = GATE_TEXTS[language] || GATE_TEXTS.en;
  const { minPassPrice, plans } = useSubscription();

  if (!gateState) return null;

  const { reason, used, limit, minimumPass } = gateState;
  const isSignup = reason === GATE_REASONS.SIGNUP_REQUIRED;
  const isLimit = reason === GATE_REASONS.LIMIT_REACHED;
  const isPassRequired = reason === GATE_REASONS.PASS_REQUIRED;

  const go = (path) => {
    onClose();
    navigate(path);
  };

  let title = txt.signupTitle;
  let subtitle = used > 0 ? txt.signupSubtitleLimit : txt.signupSubtitle;
  let icon = <RocketLaunchRoundedIcon />;
  let color = 'primary';

  if (isLimit) {
    title = txt.limitTitle;
    subtitle = txt.limitSubtitle.replace('{used}', used || 0).replace('{limit}', limit || 0);
    icon = <TimerOffRoundedIcon />;
    color = 'warning';
  } else if (isPassRequired) {
    title = txt.passTitle;
    // Cheapest plan on sale that unlocks this feature
    const unlocks = (p) => {
      const l = p.limits || {};
      const f = p.features || {};
      if (gateState.actionCode === 'export_pdf') return !!(l.can_export_pdf || f.pdf_export);
      if (gateState.actionCode === 'compare_scenarios') return !!(l.can_compare_scenarios || f.compare_scenarios);
      return true;
    };
    const plan = (plans || [])
      .filter((p) => p.plan_code !== 'free' && Number(p.price) > 0 && unlocks(p))
      .sort((a, b) => Number(a.price) - Number(b.price))[0];
    const planName = plan ? (language === 'my' ? plan.name_my || plan.name_en : plan.name_en) : null;
    subtitle = txt.passSubtitle.replace('{pass}', planName || txt.passNames?.[minimumPass] || 'Access Pass');
    icon = <LockRoundedIcon />;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <IconButton onClick={onClose} aria-label={txt.maybeLater} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseRoundedIcon />
      </IconButton>
      <DialogContent sx={{ p: 3, pt: 4 }}>
        <IconTile icon={icon} color={color} size={56} sx={{ mb: 2 }} />
        <Typography variant="h5">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {subtitle}
        </Typography>

        <List disablePadding sx={{ my: 2 }}>
          {(isSignup ? txt.signupBenefits : txt.benefits).map((benefit) => (
            <ListItem key={benefit} disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <CheckRoundedIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={benefit} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>

        {isSignup ? (
          <>
            <Button fullWidth variant="contained" size="large" onClick={() => go(`/auth?mode=signup&next=${encodeURIComponent(location.pathname)}`)}>
              {txt.signUp}
            </Button>
            <Button fullWidth onClick={() => go(`/auth?next=${encodeURIComponent(location.pathname)}`)} sx={{ mt: 1 }}>
              {txt.signIn}
            </Button>
          </>
        ) : (
          <>
            <Button fullWidth variant="contained" size="large" onClick={() => go('/app/account#passes')}>
              {txt.upgrade}
            </Button>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center', mt: 1 }}>
              {language === 'my'
                ? `Bayar sekali${minPassPrice ? ` dari RM${minPassPrice}` : ''} · Tiada langganan`
                : `Pay once${minPassPrice ? ` from RM${minPassPrice}` : ''} · No subscription`}
            </Typography>
          </>
        )}
        <Button fullWidth onClick={onClose} sx={{ mt: 1, color: 'text.secondary' }}>
          {txt.maybeLater}
        </Button>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled">
            {txt.poweredBy}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GateModal;
